export class ApiError extends Error {
  status: number;
  code: string;
  details?: unknown;
  // Integer seconds from the Retry-After header (429 rate limits, 503
  // submission_busy), else null. Device clients wait that long before retrying.
  retryAfter: number | null = null;
  constructor(status: number, code: string, message: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export function retryAfterSeconds(header: string | null | undefined): number | null {
  if (!header) return null;
  const n = Number(header.trim());
  return Number.isFinite(n) && n >= 0 ? Math.ceil(n) : null;
}

export type MergeConflict = { field: string; base: unknown; mine: unknown; theirs: unknown };

export class ConflictError extends ApiError {
  conflicts: MergeConflict[];
  serverRow: Record<string, unknown> | null;
  updatedBy: string | null;
  constructor(message: string, details: { conflicts?: MergeConflict[]; row?: Record<string, unknown>; updatedBy?: string | null }) {
    super(409, 'merge_conflict', message, details);
    this.name = 'ConflictError';
    this.conflicts = details.conflicts || [];
    this.serverRow = details.row || null;
    this.updatedBy = details.updatedBy ?? null;
  }
}

export type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';
  body?: unknown;
  query?: Record<string, string | number | undefined>;
  headers?: Record<string, string>;
  // Return the raw response (e.g. for CSV / PDF downloads) instead of JSON.
  raw?: boolean;
  // API origin for a client that is not served by the API (the Capacitor
  // shell, rep-app program W9). Empty or absent: same origin, as before.
  origin?: string;
  // The ERP sends a 403 password_change_required to /change-password. Device
  // clients (shared/platform/deviceClient.ts) pass false: they have no such
  // page and show their own sentence instead of leaving the app.
  redirectOnPasswordChange?: boolean;
  // Cookie policy: the ERP sends its session cookie ('same-origin', the
  // default); device clients pass 'omit' so only their Bearer token counts.
  credentials?: RequestCredentials;
  // Cancels the request (handed to fetch): a catalog row that leaves the
  // screen before its image arrives stops the download.
  signal?: AbortSignal;
};

const API_BASE = '/api/v1';

function buildUrl(path: string, query?: RequestOptions['query'], origin = ''): string {
  const normalized = path.startsWith('/') ? path : `/${path}`;
  const url = `${origin}${API_BASE}${normalized}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null && v !== '') params.set(k, String(v));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

// Static import — circular with branchesStore (which imports `api`),
// but resolved at runtime: the function is only CALLED inside
// apiRequest, never at module init, so Node/ES-module handles it.
import { getCurrentBranchId } from '@/shared/store/branchesStore';
import { loadedPath } from '@/bootEntry';
import { keepaliveFor } from './keepalive';
import { SESSION_LOST_EVENT } from './sessionLost';

// The RF shell (rf.html) has no login route to send the tab to: it is told
// through SESSION_LOST_EVENT instead (sessionLost.ts; RfApp listens).
const RF_SHELL = /^\/rf\.html$/;
let leaving = false;

function sessionLostElsewhere(): void {
  const at = loadedPath();
  if (RF_SHELL.test(at)) {
    window.dispatchEvent(new CustomEvent(SESSION_LOST_EVENT, { detail: { code: 'signed_in_elsewhere' } }));
    return;
  }
  // The ERP shell: the login page with the reason, like the idle sign-out.
  // A tab already on /login (its own login attempt cannot get this code, but
  // a stale call racing it could) stays where it is.
  if (at === '/login' || leaving) return;
  leaving = true;
  // D1: the next user never replays this session's pages. Imported here, not
  // at the top: the device clients share this module and never need it.
  import('@/shared/erpSession').then((m) => m.endErpSession()).catch(() => { /* leaving anyway */ })
    .finally(() => window.location.assign('/login?reason=elsewhere'));
}

export async function apiRequest<T = unknown>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = buildUrl(path, opts.query, opts.origin);
  const isJson = opts.body !== undefined && !(opts.body instanceof FormData);
  const branchId = getCurrentBranchId();
  const body: BodyInit | undefined = opts.body === undefined
    ? undefined
    : isJson
      ? JSON.stringify(opts.body)
      : (opts.body as BodyInit);
  const res = await fetch(url, {
    method: opts.method || 'GET',
    credentials: opts.credentials ?? 'same-origin',
    ...(opts.signal ? { signal: opts.signal } : {}),
    ...(keepaliveFor(body) ? { keepalive: true } : {}),
    headers: {
      ...(isJson ? { 'Content-Type': 'application/json' } : {}),
      ...(branchId ? { 'X-Branch-Id': branchId } : {}),
      ...(opts.headers || {}),
    },
    body,
  });

  if (opts.raw) {
    return res as unknown as T;
  }

  if (res.status === 204) return undefined as T;

  const contentType = res.headers.get('content-type') || '';
  const payload = contentType.includes('application/json')
    ? await res.json().catch(() => null)
    : await res.text();

  if (!res.ok) {
    const code = (payload && typeof payload === 'object' && 'error' in payload &&
      (payload as { error?: { code?: string } }).error?.code) || 'unknown';
    const message = (payload && typeof payload === 'object' && 'error' in payload &&
      (payload as { error?: { message?: string } }).error?.message) || `HTTP ${res.status}`;
    const details = (payload && typeof payload === 'object' && 'error' in payload
      ? (payload as { error?: { details?: unknown } }).error?.details
      : undefined);
    if (res.status === 409 && code === 'merge_conflict') {
      throw new ConflictError(message, (details as { conflicts?: MergeConflict[]; row?: Record<string, unknown>; updatedBy?: string | null }) || {});
    }
    // The server refuses everything but the change-password flow while an
    // admin-issued password is in force; send the tab there instead of
    // surfacing a wall of 403s. The tab already there (loaded at
    // /change-password; the address bar itself reads "/") is not sent again.
    if (res.status === 403 && code === 'password_change_required' && opts.redirectOnPasswordChange !== false
        && typeof window !== 'undefined' && loadedPath() !== '/change-password') {
      window.location.assign('/change-password');
    }
    // One browser session per user: a later sign-in of this account evicted
    // this session, and the server says so once (401 signed_in_elsewhere).
    if (res.status === 401 && code === 'signed_in_elsewhere' && typeof window !== 'undefined') {
      sessionLostElsewhere();
    }
    const err = new ApiError(res.status, code, message, details);
    err.retryAfter = retryAfterSeconds(res.headers.get('Retry-After'));
    throw err;
  }

  if (payload && typeof payload === 'object' && 'data' in payload) {
    return (payload as { data: T }).data;
  }
  return payload as T;
}

export const api = {
  get: <T = unknown>(path: string, query?: RequestOptions['query']) =>
    apiRequest<T>(path, { method: 'GET', query }),
  post: <T = unknown>(path: string, body?: unknown) =>
    apiRequest<T>(path, { method: 'POST', body }),
  patch: <T = unknown>(path: string, body?: unknown) =>
    apiRequest<T>(path, { method: 'PATCH', body }),
  put: <T = unknown>(path: string, body?: unknown) =>
    apiRequest<T>(path, { method: 'PUT', body }),
  del: <T = unknown>(path: string) =>
    apiRequest<T>(path, { method: 'DELETE' }),
};
